<script setup>
import { useField } from 'vee-validate'

const props = defineProps({
    radioButtonConfig: {
        type: Object,
        required: false,
        default: () => ({}),
        validator: (rawValue) => {
            const validations = {
                label: value => value.label === undefined || typeof value.label === 'string',
                id: value => value.id === undefined || typeof value.id === 'string',
                name: value => value.name === undefined || typeof value.name === 'string',
                value: value => value.value === undefined || typeof value.value === 'string',
                required: value => value.required === undefined || typeof value.required === 'boolean',
                val: value => value.val === undefined || typeof value.val === 'string',
                icon: value => value.icon === undefined || ['object', 'string'].includes(typeof value.icon),
                iconClass: value => value.iconClass === undefined || typeof value.iconClass === 'string',
                validationRules: value => value.validationRules === undefined || typeof value.validationRules === 'object',
                axis: value => value.axis === undefined || typeof value.axis === 'string',
                alignment: value => value.alignment === undefined || typeof value.alignment === 'string'
            }

            return Object.values(validations).every(validate => validate(rawValue))
        }
    }
})

//  Creates a form field with its validation state, you will use this inside your custom input components.
const { value, errorMessage, meta, resetField } = useField(() =>
    props.radioButtonConfig?.name,
props.radioButtonConfig?.validationRules
)

// Show error + success classes in case fields have validation rules
const _requiresValidation = ref(props.radioButtonConfig?.validationRules)

const requiresValidation = computed(() => _requiresValidation.value !== undefined)

const formFieldClass = computed(() => {
    return {
        'form-error': !meta.valid && meta.dirty && requiresValidation.value,
        'form-success': meta.valid && meta.dirty && requiresValidation.value,
        'form-input-radio-group-axis-vertical': props?.radioButtonConfig?.axis === 'vertical',
        'form-input-radio-group-alignment-vertical': props?.radioButtonConfig?.alignment === 'center'
    }
})

// Change initial value with dynamic data of input field
resetField({
    value: props.radioButtonConfig?.val
})

// Use watcher to update data with dynamic values (seems to be necessary...?)
watch(() => props.radioButtonConfig?.val, () => {
    resetField({
        value: props.radioButtonConfig?.val
    })
})
</script>

<template>
    <div class="form-input-radio-wrapper" :class="formFieldClass">
        <input
            :id="radioButtonConfig.id"
            v-model="value"
            class="form-input-radio"
            type="radio"
            :value="radioButtonConfig.value"
            :required="radioButtonConfig.required"
            :name="radioButtonConfig.name"
        >
        <label class="form-input-radio-label-wrapper" :for="radioButtonConfig?.id">
            <span class="form-input-radio-label">
                <component :is="radioButtonConfig.icon" class="form-input-radio-icon" :class="radioButtonConfig?.iconClass" :v-if="radioButtonConfig.icon" />
                {{ radioButtonConfig.label }}
            </span>
        </label>

        <FormFieldMessage
            :msg-config="{
                message: errorMessage,
                error: (!!errorMessage) && meta.dirty
            }"
        />
    </div>
</template>

<style lang="scss">
.form-input-radio-wrapper {
    position: relative;
    margin: 0 0 20px 20px;

    &.form-input-radio-group-axis-vertical {
        width: 100%;
    }
}

.form-input-radio {
    opacity: 0;
    position: absolute;
    z-index: -1;

    &:checked {
        + label {
            &::after {
                opacity: 1;
            }
        }
    }
}

.form-input-radio-icon {
    margin-inline-end: px2rem(10);
    padding-inline-start: 6px;

    &.icon-credit-card {
        height: 32px;
    }

    &.icon-apple-pay {
        height: 48px;
    }

    &.icon-google-pay {
        height: 48px;
    }
}

.form-input-radio-label-wrapper {
    min-height: 28px;
    margin: 0;
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    width: auto;

    &::before {
        content: '';
        width: 28px;
        height: 28px;
        border: $form-field-radio-label-border 2px solid;
        background: $form-field-radio-label-bg;
        border-radius: 100%;
        position: absolute;
        display: block;
        top: 0;
        left: 0;

        .form-input-radio-group-alignment-vertical & {
            top: 50%;
            transform: translateY(-50%);
        }
    }

    &::after {
        content: '';
        width: 18px;
        height: 18px;
        border-radius: 100%;
        background: $form-field-radio-label-checked-bg;
        display: block;
        position: absolute;
        top: 5px;
        left: 5px;
        opacity: 0;
        transition: opacity 400ms ease;

        .form-input-radio-group-alignment-vertical & {
            top: 50%;
            transform: translateY(-50%);
        }
    }
}

.form-input-radio-label {
    padding-inline-start: 34px;
    display: flex;
    align-items: center;

    @include body2-medium;
}
</style>
